import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Field, Select } from '@/components/live';
import { RecordDrawer } from '@/components/RecordDrawer';
import { callRpc } from '@/api/resources';
import { plotOptions } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';
import { PlotMap, type AssetType, type PlotGeometry } from '@/screens/parts/PlotMap';
import { PlotAssetsPanel, usePlotAssets } from '@/screens/parts/PlotAssetsPanel';

/**
 * Mark assets and risks on a plot, from the Risk assessment register itself (owner, 2026-09-27:
 * "can we keep the risk assessment and plot page as seperate as both are overlapping and wired
 * together?"). Choose the plot; its boundary is shown, locked - it is changed on Plots, not here -
 * and "Mark asset / risk" draws a point, line or area on or inside it, with the details asked for
 * below. The plot's assets already on record are listed, and can be removed.
 */
export function RiskMarking({ plotId: startPlot, onClose }: { plotId?: string | null; onClose: () => void }) {
  const [plotId, setPlotId] = useState(startPlot ?? '');
  const [pending, setPending] = useState<{ type: AssetType; geometry: { type: string; coordinates: unknown } } | null>(null);
  const plots = useQuery({ queryKey: ['picker', 'plots', 'with-farmer'], queryFn: () => plotOptions(), enabled: isSupabaseConfigured });
  const geometry = useQuery({
    queryKey: ['plot-geometry', plotId],
    queryFn: () => callRpc<{ type: string; geometry?: PlotGeometry } | null>('fn_plot_geojson', { p_plot_id: plotId }),
    enabled: isSupabaseConfigured && Boolean(plotId),
  });
  const assets = usePlotAssets(plotId || null);
  const plot = (plots.data ?? []).find((row) => row.id === plotId);

  return (
    <RecordDrawer footer={<button onClick={onClose} type="button">Done</button>} onClose={onClose} open record={null}
                  subtitle="Mark on the plot's map: a point, a line or an area, then say what it is and what is done about it."
                  title="Mark asset / risk" wide>
      <Field label="Plot" required>
        <Select onChange={(value) => { setPlotId(value); setPending(null); }} options={(plots.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
                placeholder="Select the plot" value={plotId} />
      </Field>
      {plotId && (
        <>
          {!geometry.isLoading && !geometry.data?.geometry && (
            <p className="muted" style={{ margin: 0 }}>This plot has no boundary yet: assets can still be marked, and the boundary is drawn on Plots.</p>
          )}
          <PlotMap
            assetTypes={assets.types.data ?? []}
            assets={assets.shapes}
            key={plotId}
            onMarkAsset={(type, shape) => setPending({ type, geometry: shape })}
            placeHint={null}
            plotCode={plot?.plot_code}
            value={geometry.data?.geometry ?? null}
          />
          <PlotAssetsPanel groupOf={assets.groupOf} onDone={() => setPending(null)} pending={pending} plotId={plotId} rows={assets.rows.data ?? []} />
        </>
      )}
    </RecordDrawer>
  );
}
